/**
 * Deposits & Funding Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const DEPOSITS_ARTICLES_ES: HelpArticle[] = [
  {
    id: "dep-001",
    slug: "how-deposits-work",
    title: "Cómo funcionan los depósitos",
    description: "Entendiendo el proceso de depósito y conversión de criptomonedas",
    category: "deposits",
    popular: true,
    order: 1,
    keywords: ["depósito", "fondeo", "cripto", "convertir", "proceso"],
    content: `
# Cómo funcionan los depósitos

Supabank hace que convertir cripto a fiat sea simple y automático.

## Resumen

1. **Crear dirección** - Genera una dirección de liquidación única
2. **Enviar cripto** - Transfiere criptomonedas soportadas a la dirección
3. **Detección automática** - Detectamos tu depósito en la blockchain
4. **Conversión** - El cripto se convierte a fiat a precio de mercado
5. **Pago** - El fiat se envía a tu cuenta bancaria vinculada

## El proceso de liquidación

### Paso 1: Creación de dirección
- Cada dirección es única para ti
- Múltiples direcciones para diferentes redes
- Las direcciones nunca expiran

### Paso 2: Enviar cripto
- Usa cualquier wallet para enviar
- Asegúrate de seleccionar la red correcta
- Incluye suficiente gas/comisiones

### Paso 3: Confirmación
- Esperamos las confirmaciones de la red
- Típicamente 1-12 confirmaciones según la red
- Verás el estado "Pendiente"

### Paso 4: Conversión
- Conversión automática a la tasa actual
- No necesitas hacer nada
- Tasas de cambio competitivas

### Paso 5: Liquidación
- El fiat se añade a tu saldo
- Pago automático al banco (si está configurado)
- O retiro manual

## Tiempos

| Red | Confirmaciones | Tiempo típico |
|-----|----------------|---------------|
| Ethereum | 12 | ~3 minutos |
| Polygon | 128 | ~5 minutos |
| Base | 12 | ~30 segundos |
| Solana | 32 | ~15 segundos |

## Notas importantes

⚠️ **Verifica siempre la red** antes de enviar
⚠️ **Depósito mínimo**: Varía según activo y red (usa la calculadora)
⚠️ **Depósito máximo**: Según tu nivel de verificación
    `.trim(),
  },
  {
    id: "dep-002",
    slug: "supported-cryptocurrencies",
    title: "Criptomonedas soportadas",
    description: "Lista de criptomonedas que puedes depositar",
    category: "deposits",
    order: 2,
    keywords: ["cripto", "monedas", "tokens", "soportadas", "divisas"],
    content: `
# Criptomonedas soportadas

Supabank acepta varias criptomonedas en múltiples redes.

## Activos soportados

Supabank soporta **8 activos** para direcciones de liquidación:

### Stablecoins
| Activo | Redes |
|--------|-------|
| USDC | Ethereum, Polygon, Base, Solana, Arbitrum, Stellar |
| USDT | Ethereum, Tron |
| PYUSD | Ethereum, Solana |
| USDB | Base, Solana |
| EURC | Base, Solana |
| DAI | Ethereum |
| USDP | Solana |

### Criptomonedas
| Activo | Redes |
|--------|-------|
| ETH | Ethereum, Base, Arbitrum, Optimism |

**Importante:** Solo los 8 activos listados están soportados para direcciones de liquidación. Bitcoin (BTC), SOL, MATIC y otras criptomonedas **NO están soportadas** como activos de depósito (aunque algunas sí como redes).

## Soporte de redes

### Redes EVM
- Ethereum (ETH)
- Polygon (MATIC)
- Base
- Arbitrum One
- Optimism
- Avalanche C-Chain

### Redes No-EVM
- Solana
- Tron
- Stellar (próximamente)

## Mínimos por activo

Los montos mínimos de depósito **varían según activo y red**. No hay un mínimo universal fijo.

### Por qué varían los mínimos
- **Comisiones de red:** Redes con comisiones altas (Ethereum) requieren mínimos mayores
- **Tipo de activo:** Stablecoins vs ETH tienen requisitos diferentes
- **Condiciones del mercado:** La congestión de la red afecta los mínimos

### Mínimos típicos
| Tipo de red | Mínimo típico |
|-------------|---------------|
| Comisiones bajas (Base, Polygon, Solana) | ~$10 |
| Comisiones altas (Ethereum mainnet) | ~$20+ |

### Ejemplos
| Activo | Red | Mínimo típico |
|--------|-----|---------------|
| USDC | Base | ~$10 |
| USDC | Ethereum | ~$20+ |
| USDT | Ethereum | ~$20+ |
| ETH | Ethereum | ~0.003 ETH |
| USDC | Solana | ~$10 |

**Usa siempre la calculadora** en tu panel para conocer el mínimo exacto para tu combinación específica de activo y red antes de enviar.

## Activos no soportados

**No envíes:**
- NFTs
- Tokens no soportados
- Activos en la red incorrecta

Enviar activos no soportados puede resultar en pérdida permanente.

## Añadir nuevos activos

Regularmente añadimos nuevos activos basándonos en:
- Demanda de usuarios
- Liquidez disponible
- Cumplimiento normativo
- Viabilidad técnica

Solicita nuevos activos a través de soporte.
    `.trim(),
  },
  {
    id: "dep-003",
    slug: "network-chain-selection",
    title: "Selección de red/blockchain",
    description: "Cómo elegir la red correcta para tu depósito",
    category: "deposits",
    order: 3,
    keywords: ["red", "cadena", "blockchain", "selección", "elegir"],
    content: `
# Selección de red/blockchain

Elegir la red correcta es crucial para depósitos exitosos.

## Por qué importa la red

- Cada blockchain es independiente
- El mismo activo existe en múltiples redes
- Red incorrecta = fondos perdidos
- Diferentes comisiones y velocidades

## Redes disponibles

### Ethereum
- La más ampliamente soportada
- Comisiones de gas más altas
- Máxima seguridad
- ~3 minutos de finalidad

### Polygon
- Comisiones bajas
- Confirmaciones rápidas
- Compatible con EVM
- Ideal para stablecoins

### Base
- Comisiones muy bajas
- Construida por Coinbase
- Transacciones rápidas
- Ecosistema en crecimiento

### Solana
- Ultra rápida
- Comisiones muy bajas
- No-EVM
- Formato de dirección diferente

## Elegir una red

### Para comisiones bajas
1. Solana
2. Base
3. Polygon
4. Arbitrum

### Para seguridad
1. Ethereum
2. Base
3. Polygon
4. Arbitrum

### Para velocidad
1. Solana
2. Base
3. Polygon
4. Arbitrum

## Errores comunes

**Enviar ETH a dirección de Polygon**
Cada dirección de red es única. Siempre haz coincidir la red.

**Usar el estándar de token incorrecto**
USDC en Ethereum ≠ USDC en Solana (contratos diferentes)

**Gas insuficiente**
Asegúrate de tener tokens nativos para comisiones

## Cómo verificar la red

En Supabank:
1. Ve a tu dirección de liquidación
2. La red se muestra claramente
3. Solo envía activos de la red correspondiente

En tu wallet:
1. Verifica la red seleccionada
2. Confirma el contrato del token
3. Confirma antes de enviar
    `.trim(),
  },
  {
    id: "dep-004",
    slug: "deposit-processing-times",
    title: "Tiempos de procesamiento de depósitos",
    description: "Cuánto tardan los depósitos en procesarse",
    category: "deposits",
    order: 4,
    keywords: ["procesamiento", "tiempo", "duración", "espera", "confirmación"],
    content: `
# Tiempos de procesamiento de depósitos

Entiende cuánto tarda en procesarse tu depósito.

## Etapas de procesamiento

### 1. Detección en blockchain (Instantáneo - 1 min)
- Detectamos tu transacción
- Estado: "Detectando"

### 2. Confirmaciones (Varía según red)
- Esperando confirmaciones de la blockchain
- Estado: "Confirmando"

### 3. Conversión (1-2 minutos)
- Convirtiendo cripto a fiat
- Estado: "Convirtiendo"

### 4. Liquidación (Instantáneo)
- Fiat añadido al saldo
- Estado: "Completado"

## Tiempos de confirmación por red

| Red | Confirmaciones | Tiempo típico |
|-----|----------------|---------------|
| Ethereum | 12 | 2-5 min |
| Polygon | 128 | 3-8 min |
| Base | 12 | 30 seg - 1 min |
| Arbitrum | 12 | 1-3 min |
| Solana | 32 | 15-30 seg |

## Factores que afectan la velocidad

### Congestión de red
- Mucho tráfico = más lento
- Verifica el estado de la red
- Horas pico: 9am-5pm UTC

### Precio de gas usado
- Más gas = más rápido
- Poco gas puede retrasar la inclusión
- Algunas wallets optimizan automáticamente

### Monto
- Montos grandes pueden necesitar verificación extra
- La verificación estándar es automática

## Seguir tu depósito

1. Ver en la pestaña Transacciones
2. Haz clic para ver el estado detallado
3. Ver el progreso de confirmaciones en blockchain
4. Se muestra el tiempo estimado de finalización

## Depósitos retrasados

Si el depósito tarda más de lo esperado:
1. Verifica el explorador de blockchain
2. Confirma que la transacción se completó
3. Asegúrate de usar la red correcta
4. Contacta con soporte si hay retraso > 1 hora
    `.trim(),
  },
  {
    id: "dep-005",
    slug: "minimum-deposit-amounts",
    title: "Montos mínimos de depósito",
    description: "Montos mínimos para depósitos de criptomonedas",
    category: "deposits",
    order: 5,
    keywords: ["mínimo", "monto", "depósito", "límite", "umbral"],
    content: `
# Montos mínimos de depósito

Todos los depósitos deben cumplir con los umbrales mínimos para ser procesados.

## Entendiendo los mínimos

### Mínimos variables

Los montos mínimos de depósito **NO son fijos** - varían según:
- **Tipo de activo:** Stablecoins vs ETH tienen requisitos diferentes
- **Red:** Ethereum mainnet tiene mínimos más altos que las redes Layer 2 (Base, Polygon)
- **Condiciones del mercado:** La congestión de red puede afectar los mínimos

**Usa siempre la calculadora** en tu panel para conocer el mínimo exacto para tu combinación específica de activo y red antes de enviar.

### Por qué existen los mínimos

- Las comisiones de red hacen que los depósitos pequeños sean ineficientes económicamente
- Costos de procesamiento por transacción
- Requisitos de cumplimiento normativo
- Mantenimiento de la calidad del servicio

## Cómo se calculan los mínimos

Los mínimos se determinan por:
1. **Mínimo base del activo** (varía según tipo de activo)
2. **Requisitos específicos de red** (Ethereum requiere mínimos más altos por los costos de gas)
3. **Conversión de moneda** (si depositas a fiat no-USD)

### Mínimos típicos por red

**Redes de comisiones bajas (Base, Polygon, Solana):**
- USDC/USDT: ~$10
- ETH: ~0.003 ETH

**Redes de comisiones altas (Ethereum mainnet):**
- USDC/USDT: ~$20+
- ETH: ~0.003 ETH (~$10-15)

**Nota:** Estos son estimados. Usa la calculadora para montos exactos.

## ¿Qué pasa por debajo del mínimo?

### Depósitos por debajo del mínimo
- Transacción detectada en blockchain
- No se procesa automáticamente
- Contacta con soporte para asistencia
- Puede acreditarse después de deducir comisiones

### Montos pequeños (< $1-5)
- Pueden no ser recuperables
- Las comisiones de red suelen exceder el valor del depósito
- Evita enviar montos muy pequeños

## Combinar depósitos

No puedes combinar múltiples depósitos pequeños. Cada transacción debe cumplir el mínimo independientemente.

## Consejos

Calcula el valor en USD antes de enviar
Incluye algo de margen sobre el mínimo
Verifica las tasas de cambio actuales
Considera las comisiones de red en el total
    `.trim(),
  },
  {
    id: "dep-006",
    slug: "failed-deposits-troubleshooting",
    title: "Solución de problemas de depósitos fallidos",
    description: "Qué hacer cuando un depósito falla",
    category: "deposits",
    order: 6,
    keywords: ["fallido", "error", "solución", "problema", "atascado"],
    content: `
# Solución de problemas de depósitos fallidos

A veces los depósitos no salen como se espera. Aquí te explicamos cómo resolver problemas.

## Problemas comunes

### 1. Red incorrecta
**Problema:** Enviaste a la dirección correcta pero en la red incorrecta

**Solución:**
- Los fondos pueden ser recuperables (varía según el caso)
- Contacta con soporte con los detalles de la transacción
- La recuperación puede tardar y tener comisiones

### 2. Por debajo del mínimo
**Problema:** Depósito por debajo del mínimo específico de la red

**Solución:**
- Transacción detectada pero no procesada automáticamente
- Contacta con soporte para revisión manual
- Pueden deducirse comisiones de la recuperación

### 3. Token no soportado
**Problema:** Enviaste un token no soportado

**Solución:**
- La recuperación puede no ser posible
- Contacta con soporte inmediatamente
- Incluye la dirección del contrato del token

### 4. Confirmaciones insuficientes
**Problema:** Transacción confirmada pero el depósito no aparece

**Solución:**
- Espera a las confirmaciones requeridas
- Verifica el estado de la red
- Generalmente se resuelve en 30 minutos

## Pasos de solución de problemas

### Paso 1: Verificar la transacción
1. Obtén el hash de tu transacción
2. Verifica en el explorador de blockchain
3. Confirma que la transacción fue exitosa
4. Anota: confirmaciones, red, monto

### Paso 2: Verificar en Supabank
1. Ve a Transacciones
2. Busca depósitos pendientes
3. Verifica el estado y progreso
4. Anota cualquier mensaje de error

### Paso 3: Contactar con soporte
Si el depósito no aparece después de 1 hora:
1. Envía correo a support@supabank.xyz
2. Incluye:
   - Hash de transacción
   - Red utilizada
   - Monto enviado
   - Tu correo de Supabank
   - Captura de pantalla de la transacción

## Tiempos de recuperación

| Problema | Resolución típica |
|----------|-------------------|
| Confirmación retrasada | 1-2 horas |
| Por debajo del mínimo | 1-2 días hábiles |
| Red incorrecta | 3-7 días hábiles |
| Token no soportado | Caso por caso |

## Prevención

Verifica dos veces la red antes de enviar
Verifica la dirección cuidadosamente
Envía un monto de prueba primero para depósitos grandes
Usa solo activos soportados
    `.trim(),
  },
  {
    id: "dep-007",
    slug: "deposit-confirmations",
    title: "Confirmaciones de depósitos",
    description: "Entendiendo las confirmaciones de blockchain",
    category: "deposits",
    order: 7,
    keywords: ["confirmaciones", "blockchain", "pendiente", "confirmado", "verificar"],
    content: `
# Confirmaciones de depósitos

Aprende sobre las confirmaciones de blockchain y por qué importan.

## ¿Qué son las confirmaciones?

Cuando envías cripto:
1. La transacción se transmite a la red
2. Se incluye en un bloque (1 confirmación)
3. Se añaden más bloques encima
4. Cada nuevo bloque = 1 confirmación más

## Por qué importan las confirmaciones

- Previenen el doble gasto
- Aseguran la finalidad de la transacción
- Protegen contra reorganizaciones
- Requeridas por seguridad

## Confirmaciones requeridas

| Red | Confirmaciones | Por qué |
|-----|----------------|---------|
| Ethereum | 12 | Estándar de alta seguridad |
| Polygon | 128 | Modelo de seguridad PoS |
| Base | 12 | L2 con seguridad de ETH |
| Arbitrum | 12 | L2 con seguridad de ETH |
| Solana | 32 | Finalidad rápida |
| Tron | 20 | Estándar de red |

## Cronología de confirmaciones

### Ejemplo: Ethereum
- 0 conf: Transacción enviada
- 1 conf: En el primer bloque (~12 seg)
- 12 conf: Totalmente confirmada (~2-3 min)

### Ejemplo: Solana
- 0 conf: Transacción enviada
- 1 conf: Primer slot (~400ms)
- 32 conf: Totalmente confirmada (~15 seg)

## Seguimiento de confirmaciones

### En Supabank
1. Ve a Transacciones
2. Haz clic en el depósito pendiente
3. Ve la barra de progreso de confirmaciones
4. Muestra actual / requeridas

### En blockchain
1. Copia el hash de transacción
2. Visita el explorador de bloques
3. Verifica el campo "Confirmaciones"

## Transacciones atascadas

Si las confirmaciones no avanzan:
- La red puede estar congestionada
- Poco gas puede retrasar la inclusión
- Verifica la página de estado de la red
- Espera o contacta con soporte
    `.trim(),
  },
];
